import React from 'react';
import { keyBy } from 'lodash';

import {
  getParentSectionId,
  SectionConfigsMap,
  Widget,
  WidgetsIdToSectionsIdMap,
} from '@neptune/shared/widgets-domain';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries -- TODO(strict-boundaries): legacy violation, remove after refactor
import { isDynamicSectionWidget } from '@neptune/widgets/dynamic-section-domain';

import type { SectionVariant, WidgetLayout } from 'domain/widget';
import { isSectionWidget } from 'domain/widget/support/section-widget';

type SectionConfigsParams = {
  layouts?: Pick<WidgetLayout, 'id'>[];
  widgets: Widget[];
};

export const useSectionConfigs = ({ layouts, widgets }: SectionConfigsParams) =>
  React.useMemo(() => getSectionConfigs({ layouts, widgets }), [layouts, widgets]);

export function getSectionConfigs({ layouts = [], widgets }: SectionConfigsParams) {
  const widgetsMap = keyBy(widgets, 'id');
  const hierarchySectionIds = new Set(
    Object.values(widgetsMap).flatMap((widget) => {
      const parentId = getParentSectionId(widget);
      return parentId === undefined ? [] : [widget.id, parentId];
    }),
  );
  let currentSectionId: string | null = null;
  const sectionConfigsMap: SectionConfigsMap = new Map([]);
  const widgetIdToSectionIdMap: WidgetsIdToSectionsIdMap = new Map();

  for (const { id: widgetId } of layouts) {
    const widget = widgetsMap[widgetId];
    const parentId = widget && getParentSectionId(widget);
    const parent = parentId ? widgetsMap[parentId] : undefined;
    const parentSection = parent && isSectionWidget(parent) ? parent : undefined;

    if (isDynamicSectionWidget(widget)) {
      // Keep flat sections' historical scope without leaking a nested group past this header.
      if (
        hierarchySectionIds.has(widget.id) ||
        (currentSectionId !== null && hierarchySectionIds.has(currentSectionId))
      ) {
        currentSectionId = null;
      }

      addToSectionConfig(widget, sectionConfigsMap, parentSection);
      continue;
    }

    if (isSectionWidget(widget)) {
      currentSectionId = widget.id;
      addToSectionConfig(widget, sectionConfigsMap, parentSection);
      continue;
    }

    if (currentSectionId) {
      widgetIdToSectionIdMap.set(widget.id, currentSectionId);
    }
  }

  return { sectionConfigsMap, widgetIdToSectionIdMap };
}

const addToSectionConfig = (
  widget: SectionVariant,
  sectionConfigsMap: SectionConfigsMap,
  parent?: SectionVariant,
) => {
  const local = widget.options?.layoutSettings;
  const inherited = parent?.options?.layoutSettings;
  sectionConfigsMap.set(widget.id, {
    sharedStep: undefined,
    layoutSettings: inherited
      ? {
          mode: local?.mode ?? inherited.mode,
          config: {
            gridMode: {
              columnsCount:
                local?.config.gridMode.columnsCount ?? inherited.config.gridMode.columnsCount,
            },
          },
        }
      : local,
  });
};
